import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, MapPin, Clock, Users, Globe,
  DollarSign, ExternalLink, Share2, Copy, Check, Loader2
} from 'lucide-react';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventDetailPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/events/public/${slug}`);
      setEvent(response.data);
    } catch (err) {
      setError('Evento no encontrado');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('es-PY', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const isPast = (dateStr) => dateStr ? new Date(dateStr) < new Date() : false;
  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-white">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-3xl font-bold text-zinc-900 mb-4">Evento no encontrado</h1>
          <Link to="/nosotros/eventos" className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg">
            <ArrowLeft className="w-5 h-5" /> Volver a Eventos
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const eventPast = isPast(event.event_date);

  return (
    <div className="min-h-screen bg-white" data-testid="event-detail-page">
      <Navigation />
      
      {/* Hero */}
      {event.image_url && (
        <div className="relative h-[400px] bg-zinc-900">
          <img src={event.image_url} alt={event.title} className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent"></div>
        </div>
      )}

      <article className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${event.image_url ? '-mt-48 relative z-10' : 'pt-32'}`}>
        <Link to="/nosotros/eventos" className={`inline-flex items-center gap-2 mb-6 transition-colors ${event.image_url ? 'text-white/80 hover:text-white' : 'text-zinc-600 hover:text-zinc-900'}`}>
          <ArrowLeft className="w-5 h-5" /> Volver a Eventos
        </Link>

        <div className="flex flex-wrap gap-2 mb-4">
          <span className="px-3 py-1 bg-emerald-500 text-white text-sm font-medium rounded-full">{event.category}</span>
          {event.is_online && <span className="px-3 py-1 bg-blue-500 text-white text-sm rounded-full flex items-center gap-1"><Globe className="w-4 h-4" />Online</span>}
          {event.is_free && <span className="px-3 py-1 bg-green-500 text-white text-sm rounded-full">Gratis</span>}
          {eventPast && <span className="px-3 py-1 bg-zinc-500 text-white text-sm rounded-full">Finalizado</span>}
        </div>

        <h1 className={`text-3xl md:text-4xl lg:text-5xl font-bold mb-6 ${event.image_url ? 'text-white' : 'text-zinc-900'}`}>
          {event.title}
        </h1>

        {/* Event Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-8">
          {/* Info Grid */}
          <div className="grid md:grid-cols-2 gap-6 mb-8 pb-8 border-b">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg"><Calendar className="w-6 h-6 text-emerald-600" /></div>
              <div>
                <p className="text-sm text-zinc-500">Fecha</p>
                <p className="font-semibold text-zinc-900 capitalize">{formatDate(event.event_date)}</p>
                {event.end_date && event.end_date !== event.event_date && (
                  <p className="text-sm text-zinc-500">hasta {formatDate(event.end_date)}</p>
                )}
              </div>
            </div>
            
            {event.event_time && (
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-100 rounded-lg"><Clock className="w-6 h-6 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-zinc-500">Hora</p>
                  <p className="font-semibold text-zinc-900">{event.event_time}</p>
                </div>
              </div>
            )}
            
            <div className="flex items-start gap-4">
              <div className="p-3 bg-purple-100 rounded-lg"><MapPin className="w-6 h-6 text-purple-600" /></div>
              <div>
                <p className="text-sm text-zinc-500">Ubicación</p>
                <p className="font-semibold text-zinc-900">{event.location}</p>
                {event.address && <p className="text-sm text-zinc-500">{event.address}</p>}
              </div>
            </div>
            
            {!event.is_free && event.price && (
              <div className="flex items-start gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg"><DollarSign className="w-6 h-6 text-yellow-600" /></div>
                <div>
                  <p className="text-sm text-zinc-500">Precio</p>
                  <p className="font-semibold text-zinc-900">{event.price}</p>
                </div>
              </div>
            )}
            
            {event.max_attendees && (
              <div className="flex items-start gap-4">
                <div className="p-3 bg-orange-100 rounded-lg"><Users className="w-6 h-6 text-orange-600" /></div>
                <div>
                  <p className="text-sm text-zinc-500">Cupo</p>
                  <p className="font-semibold text-zinc-900">{event.max_attendees} personas</p>
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <p className="text-xl text-zinc-600 mb-8 leading-relaxed">{event.description}</p>

          {/* Content */}
          {event.content && (
            <div className="prose prose-lg max-w-none prose-emerald mb-8"
              dangerouslySetInnerHTML={{ __html: event.content }} />
          )}

          {/* Map */}
          {event.map_url && (
            <a href={event.map_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8">
              <MapPin className="w-5 h-5" /> Ver ubicación en mapa <ExternalLink className="w-4 h-4" />
            </a>
          )}

          {/* Registration Button */}
          {!eventPast && event.registration_url && (
            <div className="mt-8 pt-8 border-t">
              <a href={event.registration_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-8 py-4 bg-emerald-600 text-white font-semibold rounded-xl hover:bg-emerald-700 transition-all">
                Inscribirse al evento <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          )}

          {/* Share */}
          <div className="mt-8 pt-8 border-t">
            <p className="text-sm font-medium text-zinc-700 mb-4 flex items-center gap-2">
              <Share2 className="w-5 h-5" /> Compartir evento
            </p>
            <button onClick={copyLink} className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              copied ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
            }`}>
              {copied ? <><Check className="w-5 h-5" /> Copiado</> : <><Copy className="w-5 h-5" /> Copiar enlace</>}
            </button>
          </div>
        </div>
      </article>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default EventDetailPage;
